import type { DemandAssumptions } from '../../shared/contract/analysis.js';
import { effectiveFxRates } from '../../shared/fx-rates.js';
import { demandReadOnly, intakeCtx } from '../lib/context.js';
import { orderedCurrencyCodes } from '../lib/currencies.js';
import { elValue } from '../lib/dom.js';
import { api } from './api.js';
import { loadAnalysisReport } from './loaders.js';
import {
  analysisReport,
  assumptionsDialog,
  assumptionsMessage,
  assumptionsSubmitting,
  selectedAnalysisId,
} from './state.js';

export function openAssumptionsEditor(): void {
  const ctx = intakeCtx();
  const report = analysisReport.value;
  if (ctx === null || report === null || demandReadOnly() || assumptionsSubmitting.value) return;
  const snapshot = report.analysis.assumptionsSnapshot ?? {};
  const rates = effectiveFxRates(snapshot);
  const values: Record<string, string> = {};
  for (const code of orderedCurrencyCodes(new Set(['USD', 'CNY', ...Object.keys(rates)])))
    values[`fx:${code}`] = String(rates[code] ?? '');
  for (const field of ['customsDutyPct', 'salesTaxPct', 'inlandFreight']) {
    const value = snapshot[field];
    values[field] = typeof value === 'number' || typeof value === 'string' ? String(value) : '';
  }
  assumptionsMessage.value = '';
  assumptionsDialog.value = {
    ...ctx,
    analysisId: report.analysis.id,
    base: report.analysis.summary?.base ?? 'PKR',
    values,
  };
}

/** Blank optional fields remain unchanged; invalid numbers never reach the API. */
export function parseAssumptionsDraft(values: Record<string, string>): DemandAssumptions {
  const body: DemandAssumptions = {};
  for (const [field, value] of Object.entries(values)) {
    if (value.trim() === '') continue;
    const number = Number(value);
    if (!Number.isFinite(number) || (field.startsWith('fx:') && number <= 0))
      throw new Error('Enter valid numbers. Exchange rates must be greater than zero.');
    if (field.startsWith('fx:')) (body.fxRates ??= {})[field.slice(3)] = number;
    else if (field === 'customsDutyPct' || field === 'salesTaxPct' || field === 'inlandFreight') body[field] = number;
  }
  return body;
}

export async function saveAssumptionsEditor(root: HTMLElement): Promise<void> {
  const draft = assumptionsDialog.value;
  const ctx = intakeCtx();
  if (
    draft === null ||
    ctx === null ||
    demandReadOnly() ||
    assumptionsSubmitting.value ||
    ctx.demandId !== draft.demandId ||
    ctx.orgId !== draft.orgId ||
    selectedAnalysisId.value !== draft.analysisId
  )
    return;
  assumptionsSubmitting.value = true;
  assumptionsMessage.value = '';
  const current = (): boolean => assumptionsDialog.value === draft;
  try {
    const values: Record<string, string> = {};
    for (const input of root.querySelectorAll<HTMLElement>('[data-assumption]')) {
      const field = input.getAttribute('data-assumption');
      if (field !== null) values[field] = elValue(input);
    }
    const saved = await api.setDemandAssumptions(draft.orgId, draft.demandId, parseAssumptionsDraft(values));
    if (!saved.ok) throw new Error(saved.error ?? 'Could not save assumptions.');
    const recomputed = await api.recomputeAnalysis(draft.orgId, draft.demandId, draft.analysisId);
    if (!recomputed.ok)
      throw new Error(
        recomputed.error ?? 'Assumptions saved, but the pricing check could not be recomputed. Try Save again.',
      );
    await loadAnalysisReport(draft.analysisId, draft.demandId);
    if (current()) assumptionsDialog.value = null;
  } catch (error) {
    if (current()) assumptionsMessage.value = error instanceof Error ? error.message : 'Could not save assumptions.';
  } finally {
    assumptionsSubmitting.value = false;
  }
}
